import type { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { BrowserCacheLocation, type IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';

import type { MsalGuardConfig, MsalInstanceConfig, MsalInterceptorConfig, ProtectedResource } from './models';

export const loggerCallback = (logLevel: LogLevel, message: string, containsPii: boolean) => {
  if (containsPii) {
    return;
  }
  switch (logLevel) {
    case LogLevel.Error:
      console.error(message);
      break;
    case LogLevel.Warning:
      console.warn(message);
      break;
    default:
      return;
  }
};

export const getMsalInstanceConfig = (msalInstanceConfig: MsalInstanceConfig): IPublicClientApplication =>
  new PublicClientApplication({
    auth: {
      clientId: msalInstanceConfig.clientId,
      authority: `${msalInstanceConfig.loginUrl}${msalInstanceConfig.tenantId}/`,
      redirectUri: msalInstanceConfig.redirectUri,
      postLogoutRedirectUri: msalInstanceConfig.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage, // This configures where your cache will be stored
      // storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
      // secureCookies: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: msalInstanceConfig.loggingEnabled ? loggerCallback : undefined,
        piiLoggingEnabled: false, // do not log personal information
      },
    },
  });

export const getMsalInterceptorConfig = (msalInterceptorConfig: MsalInterceptorConfig): MsalInterceptorConfiguration => {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me*', ['user.read']);

  msalInterceptorConfig.protectedResources.forEach((protectedResource: ProtectedResource) => {
    protectedResourceMap.set(protectedResource.route, protectedResource.scopes);
  });

  return {
    protectedResourceMap,
    interactionType: msalInterceptorConfig.interactionType,
  };
};

export const getMsalGuardConfig = (msalGuardConfig: MsalGuardConfig): MsalGuardConfiguration => ({
  interactionType: msalGuardConfig.interactionType,
  loginFailedRoute: msalGuardConfig.loginFailedRoute,
  authRequest: {
    scopes: msalGuardConfig.scopes,
  },
});
